/**
 * Shared musical helpers - the same semitone math every palette's voice
 * uses so "pentatonic, consonant, capped" (PR13-D spec section 10's RUSH
 * rule) is a property of one small pure function, not something each
 * palette has to separately get right.
 */

/** Converts a semitone offset from a tonic frequency into an absolute frequency (12-tone equal temperament). */
export function semitoneToFreq(tonicHz: number, semitones: number): number {
  return tonicHz * Math.pow(2, semitones / 12);
}

/**
 * A major-pentatonic scale's semitone offsets from its tonic (0, 2, 4, 7,
 * 9 within an octave) - no half-step/tritone intervals, so any subset
 * played in order reads as consonant rather than "increasingly piercing
 * beeps". Extended one octave up so a 5-step RUSH ladder has room to rise
 * without repeating a scale degree.
 */
const MAJOR_PENTATONIC_DEGREES = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21] as const;

/**
 * The exact 5 semitone offsets RUSH x1..x5 climbs through for a given
 * palette voice - degrees 2, 3, 4, 5, 6 of the extended pentatonic scale
 * (skipping the bare tonic so RUSH x1 already feels like "a step up" from
 * silence, matching the spec's "a pleasant musical rise", never a flat
 * start). Strictly increasing by construction, capped at one octave plus
 * a fourth above the tonic - "a five-note capped progression".
 */
export const RUSH_LADDER_DEGREES: readonly [number, number, number, number, number] = [
  MAJOR_PENTATONIC_DEGREES[1],
  MAJOR_PENTATONIC_DEGREES[2],
  MAJOR_PENTATONIC_DEGREES[3],
  MAJOR_PENTATONIC_DEGREES[4],
  MAJOR_PENTATONIC_DEGREES[5],
];

/** The three star-fill notes in `COMPLETION_STORY` - a short rising triad from the same scale, distinct from the RUSH ladder's degrees so the two never sound like the same event. */
export const STAR_FILL_DEGREES: readonly [number, number, number] = [MAJOR_PENTATONIC_DEGREES[0], MAJOR_PENTATONIC_DEGREES[2], MAJOR_PENTATONIC_DEGREES[4]];
